import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import WorldMap from "@/components/WorldMap";
import ServiceCard from "@/components/ServiceCard";
import { SERVICES, METRICS, CHAPTERS, IMAGES } from "@/lib/site";

const HERO_LINES = ["Engineering the", "systems that move", "the world forward."];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const orbScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden" data-testid="hero">
      {/* Parallax background */}
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.heroSwirl}
          alt=""
          className="h-full w-full img-tech object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#e9eef5]/85 via-[#e9eef5]/75 to-[#e9eef5]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(0,122,255,0.16),transparent_55%)]" />
      </motion.div>

      {/* Floating chrome orb */}
      <motion.div
        style={{ y: orbY, scale: orbScale }}
        className="pointer-events-none absolute right-[-8%] top-[18%] hidden lg:block"
      >
        <div className="chrome-orb animate-float h-72 w-72 rounded-full xl:h-96 xl:w-96" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto flex min-h-[100svh] max-w-6xl flex-col justify-center px-6 pt-28"
      >
        <Reveal delay={0.05} className="mb-7 inline-flex w-fit items-center gap-2 rounded-full border border-slate-900/10 bg-white/50 px-4 py-2 backdrop-blur">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
          </span>
          <span className="font-mono-x text-xs tracking-[0.2em] text-slate-700">
            DUBAI · JLT — ENGINEERING STUDIO
          </span>
        </Reveal>

        <Reveal delay={0.12}>
          <h1
            className="font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-8xl"
            data-testid="hero-heading"
          >
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block py-1">
                <span className={`block ${i === 2 ? "metallic-text text-glow" : "text-slate-900"}`}>
                  {line}
                </span>
              </span>
            ))}
          </h1>
        </Reveal>

        <Reveal delay={0.2} className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
          StratFlow Tech FZCO designs and builds AI, CRM, CMS, financial and
          cloud platforms — crafted to a TP99.99 standard, delivered worldwide.
        </Reveal>

        <Reveal delay={0.28} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/contact"
            data-testid="hero-cta-primary"
            className="glossy-btn inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-semibold transition-transform duration-300 hover:scale-[1.03]"
          >
            Start a project <ArrowUpRight size={18} />
          </Link>
          <Link
            to="/services"
            data-testid="hero-cta-secondary"
            className="group inline-flex items-center gap-2 rounded-full border border-slate-900/15 bg-white/50 px-7 py-3.5 text-base font-medium text-slate-900 backdrop-blur transition-colors hover:border-slate-900/25"
          >
            Explore services
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2">
        <div className="flex h-10 w-6 items-start justify-center rounded-full border border-slate-900/20 p-1.5">
          <div className="hero-scroll-dot h-2 w-1 rounded-full bg-slate-900/40" />
        </div>
      </div>
    </section>
  );
}

function Metrics() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-24" data-testid="metrics">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-slate-900/10 bg-slate-900/[0.04] md:grid-cols-4">
        {METRICS.map((m, i) => (
          <Reveal
            key={m.label}
            delay={i * 0.08}
            className="bg-[#eef2f8] p-8 text-center"
          >
            <div className="metallic-text font-display text-4xl font-extrabold tracking-tighter md:text-5xl">
              {m.value}
            </div>
            <div className="mt-3 text-sm font-semibold text-slate-900">{m.label}</div>
            <div className="mt-1 text-xs text-slate-500">{m.sub}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16" data-testid="services-preview">
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <Reveal>
          <Overline>What we build</Overline>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Six disciplines, one obsessive standard of craft.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/services"
            className="group inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-600"
            data-testid="services-preview-cta"
          >
            All services
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-12">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={(i % 3) * 0.08} className={s.span}>
            <ServiceCard service={s} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function GlobalReach() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-24" data-testid="global-reach">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <Overline>Customers worldwide</Overline>
          <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Built in Dubai. Trusted across four continents.
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 md:text-lg">
            From our HDS Towers base in JLT, our systems run for clients in the
            Gulf, Europe, the Americas and Asia — connected by a follow-the-sun
            operations model that keeps everything online, everywhere.
          </p>
          <div className="mt-8 flex flex-wrap gap-8">
            <div>
              <div className="metallic-text font-display text-3xl font-extrabold">99.99%</div>
              <div className="text-sm text-slate-500">Uptime SLA</div>
            </div>
            <div>
              <div className="metallic-text font-display text-3xl font-extrabold">12+</div>
              <div className="text-sm text-slate-500">Countries</div>
            </div>
            <div>
              <div className="metallic-text font-display text-3xl font-extrabold">24/7</div>
              <div className="text-sm text-slate-500">Coverage</div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="aspect-[5/3] w-full">
            <WorldMap />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ManifestoTeaser() {
  return (
    <section className="relative mx-auto max-w-6xl px-6 py-24" data-testid="manifesto-teaser">
      <div className="glass relative overflow-hidden rounded-[2rem] p-8 md:p-14">
        <img
          src={IMAGES.workspace}
          alt=""
          className="absolute inset-0 h-full w-full img-tech object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#eef2f8] via-[#eef2f8]/80 to-transparent" />
        <div className="relative z-10 max-w-2xl">
          <Overline>{CHAPTERS[0].num} — Manifesto</Overline>
          <h2 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
            {CHAPTERS[0].title}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-700 md:text-lg">
            {CHAPTERS[0].body}
          </p>
          <Link
            to="/about"
            className="group mt-8 inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-600"
            data-testid="manifesto-cta"
          >
            Read our story
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="mx-auto max-w-6xl px-6 pb-8 pt-4" data-testid="cta-band">
      <Reveal className="relative overflow-hidden rounded-[2rem] border border-blue-500/20 bg-gradient-to-br from-blue-100 via-[#f3f6fa] to-[#e9eef5] p-10 text-center md:p-16">
        <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[60%] -translate-x-1/2 rounded-full bg-blue-600/20 blur-[100px]" />
        <div className="relative z-10">
          <h2 className="mx-auto max-w-2xl font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Have a system worth building right?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-slate-600 md:text-lg">
            Tell us what you&apos;re building. We&apos;ll bring the engineering.
          </p>
          <Link
            to="/contact"
            data-testid="cta-band-button"
            className="glossy-btn mt-9 inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
          >
            Get in touch <ArrowUpRight size={18} />
          </Link>
        </div>
      </Reveal>
    </section>
  );
}

function StatementBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative my-10 h-[52vh] overflow-hidden" data-testid="statement-band">
      <motion.img
        style={{ y }}
        src={IMAGES.code}
        alt=""
        className="absolute inset-0 h-[124%] w-full img-tech object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#e9eef5] via-[#e9eef5]/82 to-[#e9eef5]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(0,122,255,0.18),transparent_55%)]" />
      <div className="relative z-10 mx-auto flex h-full max-w-6xl items-center px-6">
        <Reveal>
          <Overline>Our promise</Overline>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl md:text-5xl">
            We write code like it&apos;s going to run for a decade — because
            often, it does.
          </h2>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <Metrics />
      <ServicesPreview />
      <StatementBand />
      <GlobalReach />
      <ManifestoTeaser />
      <CtaBand />
    </div>
  );
}
